import React, { useState } from 'react'
import { BackActionHeader, SendDocumentModal } from '../../../shared'
import { ActivityIndicator, Text, Button } from '../../../generic'
import { navigate } from 'gatsby-link'
import { useQuery, useApolloClient } from 'react-apollo'
import gql from 'graphql-tag'
import moment from 'moment'
import { LoanInfoContainer, Separator } from '../../../insurance/MyLoan/MyLoan.style'
import { InfoRow } from '../../../insurance/MyLoan/MyLoan'
import CreditWorthinessTip from './CreditWorthinessTip'
import LoanLikelihood from './LoanLikelihood'
import { isRequired, isValidEmail } from '../../../../lib/validators'

const GET_CREDIT_WORTHINESS_ASSESSMENT = gql`
  query($id: String!) {
    creditworthiness(id: $id) {
      id
      state
      reportNumber
      applicationDate
      paymentAmount
      report {
        id
        fileId
        file {
          id
          url
        }
      }
      tips {
        name
      }
      summary {
        canGetALoan {
          card {
            label
            probability
          }
          loan {
            label
            probability
          }
          home {
            label
            probability
          }
          car {
            label
            probability
          }
        }
        creditScore
        creditRisk {
          icon
          description
        }
        creditUtilisation
        affordability
      }
    }
  }
`

const EMAIL_CREDIT_WORTHINESS_ASSESSMENT = gql`
  query($id: String!, $email: String!) {
    emailCreditWorthinessAssessment(id: $id, email: $email)
  }
`

const stateLabels = {
  'APPLICATION': 'Application',
  'PROCESSING': 'Processing',
  'INFO_REQUIRED': 'Information Required',
  'AWAITING_PAYMENT': 'Awaiting Payment',
  'COMPLETED': 'Completed',
  'CANCELLED': 'Cancelled'
}

function CreditWorthinessAssessment({ id, location }) {
  const [emailModal, setEmailModal] = useState(false)
  const apolloClient = useApolloClient()
  const { loading, error, data } = useQuery(GET_CREDIT_WORTHINESS_ASSESSMENT, {
    variables: { id }
  })

  // Check if user came from home or dashboard
  const fromHome = location?.state?.fromHome
  const fromDashboard = location?.state?.fromDashboard

  const backTitle = fromHome ? "Home" : (fromDashboard ? "My Debt" : "Document Vault")
  const backPath = fromHome ? "/" : (fromDashboard ? "/debt" : "/documents")

  const handleEmailReport = (email) => {
    apolloClient.query({
      query: EMAIL_CREDIT_WORTHINESS_ASSESSMENT,
      variables: { id, email }
    })
  }

  if (loading || error) {
    return <>
      <BackActionHeader title={backTitle} onClick={() => navigate(backPath)} />
      <div style={{ padding: '16px' }}>
        <Text size={26} bold style={{ marginBottom: '24px', marginTop: '16px' }}>
          Credit Worthiness Assessment
        </Text>
        {loading && <ActivityIndicator />}
        {!loading && error && <Text>Error loading assessment details</Text>}
      </div>
    </>
  }

  const assessment = data.creditworthiness

  if (!assessment) {
    return <>
      <BackActionHeader title={backTitle} onClick={() => navigate(backPath)} />
      <div style={{ padding: '16px' }}>
        <Text size={26} bold style={{ marginBottom: '24px', marginTop: '16px' }}>
          Credit Worthiness Assessment
        </Text>
        <Text>Assessment not found</Text>
      </div>
    </>
  }

  const handleDownload = () => {
    if (assessment.report && assessment.report.file && assessment.report.file.url) {
      // Download the file using the URL from the report
      window.open(assessment.report.file.url, '_blank')
    } else if (assessment.report && assessment.report.fileId) {
      // Fallback to constructing URL from fileId
      window.open(`${process.env.GATSBY_API_URL}file/${assessment.report.fileId}`, '_blank')
    }
  }

  return (
    <div style={{ background: '#FFFFFF', minHeight: '100vh' }}>
      <SendDocumentModal
        title="Email Credit Worthiness Assessment"
        label="Enter your email"
        document="Credit Worthiness Assessment"
        open={emailModal}
        setOpen={setEmailModal}
        validate={[isRequired, isValidEmail]}
        onSend={handleEmailReport}
      />
      <BackActionHeader title={backTitle} onClick={() => navigate(backPath)} />

      <div style={{ padding: '16px' }}>
        <Text size={26} bold style={{ marginBottom: '4px', marginTop: '16px', lineHeight: '32px' }}>
          {assessment.reportNumber}
        </Text>

        {/* Main Details Card */}
        <Text size={16} bold style={{ marginBottom: '16px', marginTop: '24px' }}>
          Credit Worthiness Assessment
        </Text>

        <LoanInfoContainer>
          <InfoRow title="Report Number" value={assessment.reportNumber} />
          <InfoRow title="Application Date" value={moment(assessment.applicationDate).format('DD MMMM YYYY')} />
          <InfoRow title="Status" value={stateLabels[assessment.state] || assessment.state} />

          {assessment.state === 'COMPLETED' && assessment.summary && (
            <>
              <Separator />

              <InfoRow title="Credit Score" value={assessment.summary.creditScore} />

              {assessment.summary.creditRisk && (
                <InfoRow title="Credit Risk" value={assessment.summary.creditRisk.description} />
              )}

              <InfoRow title="Credit Utilisation" value={`${assessment.summary.creditUtilisation}%`} />
              <InfoRow title="Affordability" value={`R ${assessment.summary.affordability}`} />
            </>
          )}
        </LoanInfoContainer>

        {/* Download and Email Buttons */}
        {assessment.state === 'COMPLETED' && assessment.report && (assessment.report.file?.url || assessment.report.fileId) && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '24px', marginBottom: '24px', textAlign: 'center', alignItems: 'center' }}>
            <Button onClick={handleDownload} style={{background: '#229490' }}>
              Download Full Report
            </Button>
            <Button onClick={() => setEmailModal(true)} inverted>
              Email Full Report
            </Button>
          </div>
        )}

        {/* Loan Likelihood Section */}
        {assessment.state === 'COMPLETED' && assessment.summary && assessment.summary.canGetALoan && (
          <div style={{ marginBottom: '16px' }}>
            <Text size={16} bold style={{ marginBottom: '12px', marginTop: '24px', paddingLeft: '4px' }}>
              Loan Approval Likelihood
            </Text>

            {assessment.summary.canGetALoan.card && (
              <LoanLikelihood
                type="card"
                probability={assessment.summary.canGetALoan.card.probability}
                label={assessment.summary.canGetALoan.card.label}
              />
            )}

            {assessment.summary.canGetALoan.loan && (
              <LoanLikelihood
                type="loan"
                probability={assessment.summary.canGetALoan.loan.probability}
                label={assessment.summary.canGetALoan.loan.label}
              />
            )}

            {assessment.summary.canGetALoan.car && (
              <LoanLikelihood
                type="car"
                probability={assessment.summary.canGetALoan.car.probability}
                label={assessment.summary.canGetALoan.car.label}
              />
            )}

            {assessment.summary.canGetALoan.home && (
              <LoanLikelihood
                type="home"
                probability={assessment.summary.canGetALoan.home.probability}
                label={assessment.summary.canGetALoan.home.label}
              />
            )}
          </div>
        )}

        {/* Tips Section */}
        {assessment.state === 'COMPLETED' && assessment.tips && assessment.tips.length > 0 && (
          <div style={{ marginBottom: '16px' }}>
            <Text size={16} bold style={{ marginBottom: '12px', paddingLeft: '4px' }}>
              Insights & Recommendations
            </Text>

            {assessment.tips.map((tip, index) => (
              <CreditWorthinessTip key={index} tip={tip} />
            ))}
          </div>
        )}

        {/* Payment Required */}
        {assessment.state === 'AWAITING_PAYMENT' && (
          <div style={{
            background: '#FFF3CD',
            boxShadow: 'inset 0px 0px 10px rgba(64, 67, 87, 0.08)',
            borderRadius: '4px',
            padding: '20px',
            marginBottom: '16px'
          }}>
            <Text size={16} bold style={{ marginBottom: '8px' }}>
              Payment Required
            </Text>
            <Text size={13} style={{ marginBottom: '16px' }}>
              Your assessment is ready, but payment is still pending. Please complete your payment to receive your report.
            </Text>
            <Button onClick={() => navigate('/credit-worthiness')}>Complete Payment</Button>
          </div>
        )}

        <div style={{ height: '100px' }} />
      </div>
    </div>
  )
}

export default CreditWorthinessAssessment
